<template>
  <div class="revision-container p-3">
    <h6>
      <strong> Revision {{ title }}</strong>
    </h6>
    <div class="ec-sort dropdown-container">
      <div
        class="col-right mr-2 d-inline"
        role="group"
        aria-label="Sort Dropdown"
      >
        <div class="btn-group ec-sort-dropdown" role="group">
          <button
            class="colorPrimarySort value-display list-group-item col w-100 d-flex flex-row justify-content-md-between p-0 align-items-center"
            type="button"
            @click="toggleDropdown"
          >
            <div class="pl-2 h-100 d-flex align-items-center">
              {{ selectedLabel }}
            </div>
            <div class="ecl-select__icon ecl-select__icon-Sort">
              <svg
                class="ecl-icon ecl-icon--s ecl-icon--rotate-180 ecl-select__icon-shape"
                focusable="false"
                aria-hidden="true"
              >
                <use
                  xlink:href="../../assets/img/ecl/icons.svg#corner-arrow"
                ></use>
              </svg>
            </div>
          </button>
          <ul
            class="dropdown-menu ec-ds-dropdown-items"
            :class="{ show: dropdownVisible }"
          >
            <li v-for="option in options" :key="option.value">
              <button class="dropdown-item" @click="onSelectionChange(option)">
                {{ option.label }}
              </button>
            </li>
          </ul>
        </div>
      </div>
      <button
        class="p-0 ecl-button show-more row text-primary w-50 mt-2"
        @click="navigateToDatasetDiffDetails"
      >
        <span data-v-9aa62744="" class="ecl-button__label" data-ecl-label="true"
          >Compare Revision</span
        >
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

interface Revision {
  modified: string;
  revisionID: string;
  revisionNumber: string;
}

export const DatasetRevisionContainer = defineComponent({
  name: "DatasetRevisionContainer",
  props: {
    title: {
      type: String,
      required: true,
    },
    selectedItem: {
      type: Array as PropType<{ label: string; value: string }[]>,
      required: true,
    },
    options: {
      type: Array as PropType<{ label: string; value: string }[]>,
      required: true,
    },
    modelValue: {
      type: String,
      required: true,
    },
    revisions: {
      type: Array as PropType<Revision[]>,
      required: true,
    },
  },
  emits: ["update:modelValue"],

  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const dropdownVisible = ref(false);
    const selectedDropdownValue = ref(props.modelValue);
    let selectedLabel = ref(
      props.options.find((option) => option.value == props.modelValue)?.label ||
        ""
    );
    const toggleDropdown = () => {
      dropdownVisible.value = !dropdownVisible.value;
    };

    const getRevisionID = (revisionNumber: string) => {
      const revision = props.revisions.find(
        (item: Revision) => item.revisionNumber === revisionNumber
      );
      return revision ? revision.revisionID : null;
    };
    const onSelectionChange = (option: { label: string; value: string }) => {
      selectedLabel.value = option.label;
      selectedDropdownValue.value = option.value;
      emit("update:modelValue", selectedDropdownValue.value);
      dropdownVisible.value = false;
    };
    const { t, te } = useI18n();

    const publicationText = computed(() => {
      return te("message.metadata.datasetRevision.publication")
        ? t("message.metadata.datasetRevision.publication")
        : "Publication Date";
    });
    const otherVersionText = computed(() => {
      return te("message.metadata.datasetRevision.otherVersion")
        ? t("message.metadata.datasetRevision.otherVersion")
        : "Other versions";
    });

    const navigateToDatasetDiffDetails = async (): Promise<void> => {
      const revisionId = getRevisionID(selectedDropdownValue.value);
      router.push({
        name: "DatasetDiffDetails",
        params: {
          ds_id: route.params.ds_id,
          selectedRevisionVersion: revisionId,
        },
        query: {
          srcRevision: revisionId,
          targetRevision: "",
        },
      });
    };

    return {
      dropdownVisible,
      toggleDropdown,
      onSelectionChange,
      selectedLabel,
      publicationText,
      otherVersionText,
      navigateToDatasetDiffDetails,
      selectedDropdownValue,
    };
  },
});

export default DatasetRevisionContainer;
</script>

<style scoped lang="scss">
.revision-container {
  background-color: #f3f6fc;
  border-radius: 4px;
  margin-bottom: 25px !important;
}
.revision-container h6 {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #212529;
}
p {
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
}
.dropdown-container {
  margin-top: 5px !important;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.dropdown-container p {
  font-size: 0.95rem;
}
.ec-sort-dropdown {
  width: 300px;
  height: 48px;
  border-radius: 4px;
}
.colorPrimarySort {
  border-radius: 4px;
}
.ec-ds-dropdown-items {
  width: 300px;
  background-color: #fff !important;
  color: #7f7f7f !important;
}
.ecl-button.show-more:hover {
  background-color: #0e47cb !important;
  border: 2px solid #0e47cb !important;
}
.dropdown-item {
  margin-top: 3px;
  margin-bottom: 3px;
}
</style>
